/* Optional homepage disclosure. No Alpine dependency; source sections own their labels. */
(() => {
  'use strict';
  if (customElements.get('mw-homepage-navigation')) return;

  const visible = element => {
    if (!element || element.closest('[hidden], [inert], [x-cloak], [aria-hidden="true"]') || getComputedStyle(element).visibility === 'hidden') return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };
  const blockers = '[aria-modal="true"], dialog[open], .mw-header--menu-open, .x-search-popup:not(.popup-hidden), .promo-popup .shadow-popup, .x-cookie-pref-backdrop';
  const obstructions = '#cookies-banner, .popup-minimal, .scroll_top';

  class HomepageNavigation extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.controller) return;
        if (document.readyState === 'loading') {
          this.readyController?.abort();
          this.readyController = new AbortController();
          document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true, signal: this.readyController.signal });
          return;
        }
        this.main = document.getElementById('MainContent');
        this.trigger = this.querySelector('[data-home-nav-trigger]');
        this.panel = this.querySelector('[data-home-nav-panel]');
        this.list = this.querySelector('[data-home-nav-list]');
        this.current = this.querySelector('[data-home-nav-current]');
        this.bar = this.querySelector('.home-nav__bar');
        this.mobileClose = this.querySelector('[data-home-nav-mobile-close]');
        this.status = [...document.querySelectorAll('[data-home-nav-status]')].find(node => node.dataset.homeNavStatus === this.dataset.sectionId);
        if (!this.main || !this.trigger || !this.panel || !this.list || !this.current || !this.bar || !this.mobileClose) return;
        this.controller = new AbortController();
        this.entries = [];
        this.focusCleanup = new Set();
        this.mobileQuery = matchMedia('(max-width: 767px)');
        this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
        this.headerUntil = 0;
        this.editorSelected = false;
        this.overlayNodes = new Set();
        const on = (target, event, fn, options = {}) => target.addEventListener(event, fn, { ...options, signal: this.controller.signal });
        on(this.trigger, 'click', () => this.setOpen(this.panel.hidden));
        on(this.querySelector('[data-home-nav-close]'), 'click', () => this.setOpen(false, true));
        on(this.mobileClose, 'click', () => this.setOpen(false, true));
        on(this.list, 'click', event => {
          const link = event.target.closest('a[data-home-nav-link]');
          if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          const entry = this.entries.find(item => item.id === link.dataset.homeNavLink);
          if (!entry) return;
          event.preventDefault();
          const hash = `#${encodeURIComponent(entry.id)}`;
          if (location.hash !== hash) history.pushState(history.state, '', hash);
          this.navigate(entry.target, true);
        });
        on(document, 'pointerdown', event => { if (!this.contains(event.target)) { this.setOpen(false); this.cancelNavigation(); } });
        on(this, 'focusout', event => {
          if (event.relatedTarget) { if (!this.contains(event.relatedTarget)) this.setOpen(false); }
          else {
            clearTimeout(this.focusTimer);
            this.focusTimer = setTimeout(() => { if (!this.contains(document.activeElement)) this.setOpen(false); }, 0);
          }
        });
        on(this, 'keydown', event => {
          if (event.key === 'Escape' && !this.panel.hidden) { event.preventDefault(); event.stopPropagation(); this.setOpen(false, true); }
        });
        on(document, 'keydown', event => { if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) this.cancelNavigation(); });
        on(document, 'focusin', event => {
          if (this.hidden || !this.mobileQuery.matches || this.navigationTarget || !this.main.contains(event.target)) return;
          queueMicrotask(() => {
            if (!this.controller || this.hidden || document.activeElement !== event.target) return;
            const top = this.bar.getBoundingClientRect().bottom + 8;
            const rect = event.target.getBoundingClientRect();
            if (rect.top < top && rect.bottom > 0 && rect.height < innerHeight - top) window.scrollTo({ top: window.scrollY + rect.top - top, behavior: 'instant' });
          });
        });
        on(window, 'wheel', () => this.cancelNavigation(), { passive: true });
        on(window, 'touchstart', () => this.cancelNavigation(), { passive: true });
        on(window, 'scroll', () => {
          this.queue();
          if (this.navigationTarget) this.scheduleSettlement();
        }, { passive: true });
        on(window, 'resize', () => this.queue(true), { passive: true });
        on(window, 'pageshow', () => this.queue(true));
        on(window, 'popstate', () => this.queueHash());
        on(window, 'hashchange', () => this.queueHash());
        on(window, 'load', () => { this.queue(true); if (this.initialHash) this.queueHash(); });
        on(this.mobileQuery, 'change', () => { this.setOpen(false); this.queue(true); });
        on(document, 'eurus:sticky-header-moved', event => {
          this.headerHint = Math.max(0, Number(event.detail?.heightHeader) || 0);
          this.headerUntil = performance.now() + 500;
          this.queue();
        });
        for (const event of ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder', 'shopify:section:select', 'shopify:section:deselect', 'shopify:block:select']) {
          on(document, event, detail => {
            const ownSection = detail.detail?.sectionId === this.dataset.sectionId || detail.target === this || detail.target === this.parentElement;
            if (this.dataset.editor === 'true' && ownSection) {
              if (event === 'shopify:section:select') { this.editorSelected = true; this.editorOpenPending = true; }
              if (event === 'shopify:section:deselect' || event === 'shopify:section:unload') { this.editorSelected = this.editorOpenPending = false; this.setOpen(false); }
            }
            this.queue(true);
          });
        }
        this.resizeObserver = new ResizeObserver(() => this.queue(true));
        this.resizeObserver.observe(this.main);
        this.resizeObserver.observe(this.bar);
        this.observer = new MutationObserver(records => {
          const external = records.filter(record => !this.contains(record.target) && record.target !== this.status);
          if (!external.length) return;
          const discover = external.some(record => this.main.contains(record.target) &&
            (record.type === 'childList' || record.target === this.main || record.target.matches?.('.shopify-section, [data-homepage-nav-target]')));
          if (external.some(record => record.target.closest?.(`.section-header, .section-announcement, ${obstructions}`))) this.headerUntil = performance.now() + 550;
          this.queue(discover);
        });
        this.observer.observe(document.body, { subtree: true, childList: true, attributes: true,
          attributeFilter: ['hidden', 'inert', 'class', 'style', 'open', 'aria-hidden', 'data-homepage-nav-label', 'data-homepage-nav-target', 'data-homepage-nav-icon'] });
        this.queue(true);
        this.initialHash = !!location.hash;
        if (this.initialHash) this.queueHash();
        document.fonts?.ready.then(() => { if (this.isConnected && this.controller) this.queue(true); });
      });
    }

    queue(discover = false) {
      this.needsDiscovery ||= discover;
      if (this.frame || !this.controller) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.needsDiscovery) { this.needsDiscovery = false; this.discover(); }
        this.update();
      });
    }

    discover() {
      const seen = new Set();
      const entries = [];
      let unlabeled = this.main.querySelectorAll('[data-homepage-nav-warning]').length;
      for (const marker of this.main.querySelectorAll('[data-homepage-nav-target]')) {
        const id = marker.dataset.homepageNavTarget;
        const label = marker.dataset.homepageNavLabel?.trim();
        if (!label) unlabeled++;
        const target = document.getElementById(id);
        if (!label || seen.has(id) || !target || !this.main.contains(target) || marker.closest('.shopify-section') !== target || !visible(target)) continue;
        seen.add(id);
        const svg = marker.content?.querySelector('svg');
        const icon = `${marker.dataset.homepageNavIcon || 'none'}:${svg?.outerHTML || ''}`;
        entries.push({ id, label, target, svg, icon });
      }
      this.unlabeled = unlabeled;
      if (entries.length === this.entries.length && entries.every((entry, i) => entry.id === this.entries[i].id && entry.label === this.entries[i].label && entry.target === this.entries[i].target && entry.icon === this.entries[i].icon)) return;
      this.setOpen(false);
      for (const entry of this.entries) this.resizeObserver.unobserve(entry.target);
      this.entries = entries;
      this.list.replaceChildren(...entries.map(entry => {
        const li = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${encodeURIComponent(entry.id)}`;
        link.dataset.homeNavLink = entry.id;
        if (entry.svg && !entry.icon.startsWith('none:')) {
          const svg = entry.svg.cloneNode(true);
          svg.classList.add('home-nav__destination-icon');
          svg.setAttribute('aria-hidden', 'true');
          svg.setAttribute('focusable', 'false');
          link.append(svg);
        }
        const label = document.createElement('span');
        label.className = 'home-nav__destination-label';
        label.textContent = entry.label;
        link.append(label);
        li.append(link);
        entry.link = link;
        this.resizeObserver.observe(entry.target);
        return li;
      }));
    }

    headerHeight() {
      const nodes = [...document.querySelectorAll('#sticky-header-content, .section-announcement')];
      if (!nodes.length) return this.headerHint || 0;
      const rects = nodes.filter(visible).map(node => node.getBoundingClientRect()).sort((a, b) => a.top - b.top);
      let bottom = 0;
      for (const rect of rects) if (rect.top <= bottom + 1 && rect.bottom > bottom) bottom = rect.bottom;
      return Math.max(0, bottom);
    }

    isBlocked() {
      return document.body.classList.contains('overflow-hidden') || document.documentElement.classList.contains('overflow-hidden') ||
        [...document.querySelectorAll(blockers)].some(visible);
    }

    property(name, value) { if (this.style.getPropertyValue(name) !== value) this.style.setProperty(name, value); }

    layout(header, mobile) {
      const style = getComputedStyle(this);
      const bar = this.bar.getBoundingClientRect();
      const edge = parseFloat(style.getPropertyValue('--home-nav-vertical')) || 24;
      const width = parseFloat(style.getPropertyValue('--home-nav-width')) || 300;
      const right = this.dataset.position === 'right';
      const leftEdge = mobile ? 0 : right ? Math.min(bar.left, bar.right - width) : bar.left;
      const rightEdge = mobile ? innerWidth : right ? bar.right : Math.max(bar.right, bar.left + width);
      let ceiling = innerHeight - 8;
      const nodes = new Set(document.querySelectorAll(obstructions));
      for (const node of this.overlayNodes) if (!nodes.has(node)) this.resizeObserver.unobserve(node);
      for (const node of nodes) {
        if (!this.overlayNodes.has(node)) this.resizeObserver.observe(node);
        if (!visible(node)) continue;
        const rect = node.getBoundingClientRect();
        if (rect.right > leftEdge && rect.left < rightEdge && rect.bottom > header && rect.top < innerHeight) ceiling = Math.min(ceiling, rect.top - 12);
      }
      this.overlayNodes = nodes;
      const bottom = mobile ? 0 : Math.max(edge, innerHeight - ceiling);
      this.property('--home-nav-bottom', `${bottom}px`);
      const gap = mobile ? parseFloat(style.getPropertyValue('--home-nav-mobile-gap')) || 0 : 12;
      const available = mobile ? ceiling - header - gap - bar.height : innerHeight - bottom - bar.height - gap - header - 8;
      this.property('--home-nav-panel-max', `${Math.max(0, available)}px`);
      const border = parseFloat(style.getPropertyValue('--home-nav-border-width')) || 0;
      const row = this.dataset.density === 'compact' ? 44 : 48;
      const minimum = row + 12 + 2 * border + (mobile ? 0 : 60 + border);
      return available >= minimum;
    }

    update() {
      const mobile = this.mobileQuery.matches;
      const header = this.headerHeight();
      this.property('--home-nav-header', `${header}px`);
      let show = this.entries.length >= 2 && this.dataset[mobile ? 'mobile' : 'desktop'] === 'true' && (window.scrollY >= window.innerHeight || this.editorSelected) && !this.isBlocked();
      // Measure the actual bar, including large text/borders, before deciding fit.
      if (show) { this.hidden = false; show = this.layout(header, mobile); }
      if (this.hidden === show) this.hidden = !show;
      if (!show) this.setOpen(false);
      const bar = mobile && show ? this.bar.getBoundingClientRect().height : 0;
      this.property('--home-nav-bar', `${bar}px`);
      const gap = mobile && show ? parseFloat(getComputedStyle(this).getPropertyValue('--home-nav-mobile-gap')) || 0 : 0;
      const line = header + bar + gap + 8;
      let active;
      for (const entry of this.entries) if (entry.target.getBoundingClientRect().top <= line + 1) active = entry;
      for (const entry of this.entries) {
        if (entry === active) { if (!entry.link.hasAttribute('aria-current')) entry.link.setAttribute('aria-current', 'location'); }
        else entry.link.removeAttribute('aria-current');
      }
      const label = active?.label || '';
      if (this.current.textContent !== label) this.current.textContent = label;
      this.current.hidden = !label;
      if (this.status) {
        this.status.hidden = !this.editorSelected;
        const message = `${this.entries.length} valid destinations. ${this.unlabeled ? `${this.unlabeled} selected section(s) need a navigation label. ` : ''}${this.entries.length < 2 ? 'Choose at least two labeled, visible destinations. ' : ''}${this.dataset[mobile ? 'mobile' : 'desktop'] !== 'true' ? 'Navigation is disabled on this device.' : ''}`;
        if (this.status.textContent !== message) this.status.textContent = message;
      }
      if (show && this.editorOpenPending) { this.editorOpenPending = false; this.setOpen(true); }
      // Follow the actual header rectangle throughout its scroll-up transition.
      if (performance.now() < this.headerUntil) this.queue();
    }

    setOpen(open, restoreFocus = false) {
      if (!this.panel) return;
      if (open && this.hidden) return;
      if (restoreFocus && !this.hidden) this.trigger.focus({ preventScroll: true });
      this.panel.hidden = !open;
      this.trigger.setAttribute('aria-expanded', String(open));
      this.dataset.open = String(open);
      if (this.mobileClose) this.mobileClose.hidden = !open;
      if (open) {
        this.queue();
        requestAnimationFrame(() => {
          if (!this.controller || this.panel.hidden) return;
          const active = this.list.querySelector('[aria-current="location"]');
          if (!active) return;
          const panel = this.panel.getBoundingClientRect();
          const link = active.getBoundingClientRect();
          const heading = this.mobileQuery.matches ? 0 : this.querySelector('.home-nav__heading').getBoundingClientRect().height;
          if (link.top < panel.top + heading + 6) this.panel.scrollTop += link.top - panel.top - heading - 6;
          else if (link.bottom > panel.bottom - 6) this.panel.scrollTop += link.bottom - panel.bottom + 6;
        });
      }
    }

    targetTop(target) {
      const mobile = this.mobileQuery.matches && this.dataset.mobile === 'true' && this.entries.length >= 2;
      const gap = mobile ? parseFloat(getComputedStyle(this).getPropertyValue('--home-nav-mobile-gap')) || 0 : 0;
      // Predict the bar's occupied height even on initial fragment loads before reveal.
      const bar = mobile ? this.bar.getBoundingClientRect().height || parseFloat(getComputedStyle(this).getPropertyValue('--home-nav-height-mobile')) || 48 : 0;
      const absolute = window.scrollY + target.getBoundingClientRect().top;
      const header = this.headerHeight();
      const offset = header + ((absolute - header >= window.innerHeight) ? bar + gap : 0) + 8;
      return Math.max(0, Math.min(absolute - offset, document.documentElement.scrollHeight - window.innerHeight));
    }

    navigate(target, smooth = false) {
      this.setOpen(false);
      this.navigationTarget = target;
      window.scrollTo({ top: this.targetTop(target), behavior: smooth && !this.motionQuery.matches ? 'smooth' : 'instant' });
      const focus = target.querySelector('h1, h2, h3, h4, h5, h6') || target;
      if (!focus.hasAttribute('tabindex')) {
        focus.setAttribute('tabindex', '-1');
        const cleanup = () => { focus.removeAttribute('tabindex'); this.focusCleanup.delete(cleanup); };
        this.focusCleanup.add(cleanup);
        focus.addEventListener('blur', cleanup, { once: true, signal: this.controller.signal });
      }
      focus.focus({ preventScroll: true });
      this.scheduleSettlement();
      this.queue();
    }

    scheduleSettlement() {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => {
        if (performance.now() < this.headerUntil) { this.scheduleSettlement(); return; }
        const target = this.navigationTarget;
        this.navigationTarget = null;
        if (target?.isConnected && visible(target)) window.scrollTo({ top: this.targetTop(target), behavior: 'instant' });
        this.queue();
      }, 160);
    }

    queueHash() {
      if (this.hashFrame) cancelAnimationFrame(this.hashFrame);
      this.hashFrame = requestAnimationFrame(() => {
        this.hashFrame = null;
        if (this.needsDiscovery) { this.needsDiscovery = false; this.discover(); }
        let id;
        try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
        const entry = this.entries.find(item => item.id === id);
        if (entry) this.navigate(entry.target);
      });
    }

    cancelNavigation() {
      clearTimeout(this.settleTimer);
      if (this.navigationTarget) window.scrollTo({ top: window.scrollY, behavior: 'instant' });
      this.navigationTarget = null;
      this.initialHash = false;
    }

    disconnectedCallback() {
      this.readyController?.abort();
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.hashFrame);
      clearTimeout(this.settleTimer);
      clearTimeout(this.focusTimer);
      for (const cleanup of this.focusCleanup || []) cleanup();
      this.frame = this.hashFrame = null;
      this.controller = null;
      this.hidden = true;
      if (this.status) this.status.hidden = true;
      this.setOpen(false);
    }
  }
  customElements.define('mw-homepage-navigation', HomepageNavigation);
})();
